import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

export interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastItemProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

const iconMap: Record<ToastType, string> = {
  success: 'solar:check-circle-bold-duotone',
  warning: 'solar:danger-triangle-bold-duotone',
  error: 'solar:close-circle-bold-duotone',
  info: 'solar:info-circle-bold-duotone',
};

const colorMap: Record<ToastType, string> = {
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
};

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const duration = toast.duration || 4000;
    const exitTimer = setTimeout(() => setExiting(true), duration - 300);
    const removeTimer = setTimeout(() => onDismiss(toast.id), duration);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
    };
  }, [toast.id, toast.duration, onDismiss]);

  const color = colorMap[toast.type];

  return (
    <div
      style={{
        minWidth: '340px',
        maxWidth: '420px',
        background: 'var(--modal-bg)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--glass-border)',
        borderLeft: `3px solid ${color}`,
        borderRadius: '12px',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        boxShadow: 'var(--glass-shadow-elevated)',
        animation: exiting ? 'slideOut 0.3s ease forwards' : 'slideIn 0.3s ease',
        marginBottom: '8px',
      }}
    >
      <Icon icon={iconMap[toast.type]} width={22} style={{ color, flexShrink: 0, marginTop: '1px' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>{toast.title}</div>
        {toast.message && (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>{toast.message}</div>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '2px',
          flexShrink: 0,
        }}
      >
        <Icon icon="solar:close-circle-bold-duotone" width={18} />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default ToastContainer;
